import { Repeat, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { formatShortDate } from "@/lib/dateUtils";

interface SeriesScopeDialogProps {
  /** What is about to change — "Day of the week", "Start time". */
  changes: string[];
  /** Upcoming individually-edited nights, soonest first. */
  edited: { date: string; title: string }[];
  /** Upcoming cancelled nights. */
  cancelled: number;
  onChoose: (includeSpecial: boolean) => void;
  onCancel: () => void;
}

/** Shown before at most this many special nights start being summarised. */
const LISTED = 4;

/**
 * Asked when a series-wide save would reach nights that differ from the
 * series. Either way only the changed fields are applied: including special
 * nights moves a theme night to the new day, it does not rewrite its theme.
 */
export function SeriesScopeDialog({
  changes,
  edited,
  cancelled,
  onChoose,
  onCancel,
}: SeriesScopeDialogProps) {
  const extra = edited.length - LISTED;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl p-6 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-1">
          <Repeat className="w-4 h-4 text-primary" />
          <h2 className="font-semibold text-foreground">Apply to special nights too?</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Changing: <span className="text-foreground">{changes.join(", ")}</span>
        </p>

        <div className="my-4 rounded-lg border border-border bg-background/50 px-3 py-2 text-sm">
          {edited.length > 0 && (
            <ul className="space-y-1">
              {edited.slice(0, LISTED).map((night) => (
                <li key={night.date} className="flex gap-2">
                  <span className="text-muted-foreground shrink-0 whitespace-nowrap">
                    {formatShortDate(night.date)}
                  </span>
                  <span className="truncate text-foreground">{night.title}</span>
                </li>
              ))}
              {extra > 0 && (
                <li className="text-muted-foreground">and {extra} more</li>
              )}
            </ul>
          )}
          {cancelled > 0 && (
            <p className={edited.length > 0 ? "mt-1 text-muted-foreground" : "text-muted-foreground"}>
              {cancelled} cancelled {cancelled === 1 ? "night" : "nights"}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            className="justify-start h-auto py-3 px-4"
            onClick={() => onChoose(true)}
          >
            <Sparkles className="w-4 h-4 mr-2 shrink-0" />
            <div className="text-left">
              <div className="font-medium">All nights, including special ones</div>
              <div className="text-xs text-muted-foreground whitespace-normal">
                Only what you changed is applied — their own details stay
              </div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="justify-start h-auto py-3 px-4"
            onClick={() => onChoose(false)}
          >
            <Repeat className="w-4 h-4 mr-2 shrink-0" />
            <div className="text-left">
              <div className="font-medium">Regular nights only</div>
              <div className="text-xs text-muted-foreground whitespace-normal">
                Special nights stay exactly as they are
              </div>
            </div>
          </Button>

          <Button variant="ghost" className="mt-1" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
